/**
 * Pernod Ricard Kazakhstan · Marketing / Digital
 * Google Analytics 4 (GA4) & Google Tag Manager (GTM) integration helper
 */

// Ru / Kz alphabet transliteration mapping for city & product slugs
const ruKzToEnMap = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
    з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
    п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
    ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    ә: 'a', ғ: 'g', қ: 'k', ң: 'n', ө: 'o', ұ: 'u', ү: 'u', һ: 'h', і: 'i'
};

/**
 * Transliterates Cyrillic text to a clean snake_case slug (e.g., 'Алматы' -> 'almaty')
 * @param {string} str 
 * @returns {string}
 */
export function toSlug(str) {
    if (!str || typeof str !== 'string') return '';
    return str
        .toLowerCase()
        .trim()
        .split('')
        .map(char => (ruKzToEnMap[char] !== undefined ? ruKzToEnMap[char] : char))
        .join('')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/**
 * Pushes event/data payload to window.dataLayer
 * @param {Object} data 
 */
export function pushDataLayer(data) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    if (process.env.NODE_ENV !== 'production') {
        console.log('[Analytics dataLayer push]:', data);
    }
}

/**
 * Задача 2: Идентификаторы активации (метаданные страницы)
 * @param {Object} params
 * @param {string} params.brand - e.g., 'chivas_regal'
 * @param {string} params.activation - e.g., 'chivas_enrique_iglesias_2026'
 * @param {string} params.landing_id - e.g., 'chivas-ei-2026'
 * @param {string} params.landing_type - e.g., 'registration'
 */
export function initLanding({ brand, activation, landing_id, landing_type }) {
    pushDataLayer({
        brand: brand || 'chivas_regal',
        activation: activation || 'chivas_enrique_iglesias_2026',
        landing_id: landing_id || 'chivas-ei-2026',
        landing_type: landing_type || 'registration'
    });
}

// Track whether form_start has already been triggered on current page view
let formStarted = false;

/**
 * Сброс состояния form_start (при смене формы или маршрута)
 */
export function resetFormStart() {
    formStarted = false;
}

/**
 * Задача 3: Первый фокус / ввод в форму (один раз за сессию/загрузку)
 * @param {string} formId 
 */
export function trackFormStart(formId = 'main_registration') {
    if (formStarted) return;
    formStarted = true;
    pushDataLayer({
        event: 'form_start',
        form_id: formId
    });
}

/**
 * Задача 3: Нажатие кнопки отправки формы
 * @param {string} formId 
 */
export function trackFormSubmit(formId = 'main_registration') {
    pushDataLayer({
        event: 'form_submit',
        form_id: formId
    });
}

/**
 * Задача 3: Сообщение об ошибке валидации или сервера
 * Внимание: НИКОГДА не передает персональные данные пользователя!
 * @param {Object} params
 * @param {string} params.formId
 * @param {'validation'|'server'|'captcha'} params.errorType
 * @param {string} params.fieldName - e.g., 'phone', 'email', 'name', 'city'
 * @param {string} params.errorTextCode - e.g., 'phone_invalid_format', 'field_required'
 */
export function trackFormError({
    formId = 'main_registration',
    errorType = 'validation',
    fieldName,
    errorTextCode
}) {
    pushDataLayer({
        event: 'form_error',
        form_id: formId,
        error_type: errorType,
        field_name: fieldName,
        error_text_code: errorTextCode
    });
}

/**
 * Задача 3: Успешная регистрация (только после подтверждения сервера/OTP)
 * @param {Object} params
 * @param {string} params.formId
 * @param {string} [params.city] - Человекочитаемое или slug название города
 * @param {string} [params.productChoice] - Название выбранного продукта/релиза
 */
export function trackRegistrationSuccess({
    formId = 'main_registration',
    city = '',
    productChoice = ''
}) {
    const payload = {
        event: 'registration_success',
        form_id: formId
    };

    if (city) {
        payload.city = toSlug(city);
    }
    if (productChoice) {
        payload.product_choice = toSlug(productChoice);
    }

    pushDataLayer(payload);
}

/**
 * Задача 3: Проверка возраста
 * @param {boolean} passed 
 */
export function trackAgeGate(passed) {
    pushDataLayer({
        event: passed ? 'age_gate_pass' : 'age_gate_fail'
    });
}

/**
 * Задача 4: Виртуальный просмотр страницы в SPA (экран «спасибо»)
 * @param {string} path - e.g., '/registration/thank-you'
 * @param {string} title - e.g., 'Спасибо за регистрацию'
 */
export function trackVirtualPageView(
    path = '/registration/thank-you',
    title = 'Спасибо за регистрацию'
) {
    pushDataLayer({
        event: 'virtual_page_view',
        virtual_path: path,
        virtual_title: title
    });
}

/**
 * Задача 5: Обновление статуса согласия (Consent Mode v2)
 * @param {boolean} granted 
 */
export function trackConsent(granted) {
    const status = granted ? 'granted' : 'denied';
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            analytics_storage: status,
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
        });
    }

    pushDataLayer({
        event: 'consent_update',
        analytics_consent: status,
        ads_consent: 'denied'
    });
}
